
import React, { useState, useEffect } from 'react';
import { Person, Relationship } from '../types';
import { X, UserPlus, Shield, Baby, AlertCircle, ChevronRight, Save, Percent } from 'lucide-react';

interface AddNomineeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (person: Person) => void;
  initialData?: Person | null;
}

const INITIAL_STATE: Partial<Person> = {
  fullName: '',
  relationship: Relationship.SPOUSE,
  email: '',
  targetShare: 0,
  gender: '',
  dob: '',
  pan: '',
  aadhaar: '',
  passport: '',
  mobile: '',
  address: '',
  isMinor: false,
  guardianName: '',
  guardianRelationship: '',
  guardianPan: '',
  guardianContact: ''
};

export const AddNomineeModal: React.FC<AddNomineeModalProps> = ({ isOpen, onClose, onAdd, initialData }) => {
  const [formData, setFormData] = useState<Partial<Person>>(INITIAL_STATE);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData(INITIAL_STATE);
    }
  }, [initialData, isOpen]);

  useEffect(() => {
    if (formData.dob) {
      const birthDate = new Date(formData.dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      setFormData(prev => ({ ...prev, isMinor: age < 18 }));
    } else {
      setFormData(prev => ({ ...prev, isMinor: false }));
    }
  }, [formData.dob]);

  if (!isOpen) return null;

  const isChild = formData.relationship === Relationship.CHILD;
  // Enhanced validation: Name, Email, DOB (for children), and Guardian info (for minors) are required.
  const isFormValid = !!(
    formData.fullName && 
    formData.email && 
    (isChild ? formData.dob : true) && 
    (!formData.isMinor || (formData.guardianName && formData.guardianRelationship))
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid) {
      onAdd({ 
        ...formData as Person, 
        // Keep UUID if editing, else use a temporary ID for tracking
        id: initialData?.id || `p-${Date.now()}` 
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData(INITIAL_STATE);
    onClose();
  };

  const isEdit = !!initialData;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-2 md:p-4 animate-fade-in">
      <div className="bg-white w-full max-w-2xl rounded-2xl md:rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-200 flex flex-col max-h-[95vh] md:max-h-[90vh]">
        
        {/* Header */}
        <div className="bg-white px-6 md:px-10 py-6 md:py-8 flex justify-between items-center border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-4 md:gap-5">
             <div className="bg-indigo-600 p-2.5 md:p-3.5 rounded-xl md:rounded-[1.25rem] text-white shadow-xl shadow-indigo-100 shrink-0">
                <UserPlus size={24} className="md:w-7 md:h-7" strokeWidth={3} />
             </div>
             <div>
                <h2 className="text-lg md:text-2xl font-black text-slate-900 leading-tight">
                  {isEdit ? 'Update Profile' : 'Add Nominee'}
                </h2>
                <p className="text-slate-400 text-[9px] md:text-sm font-bold uppercase tracking-widest mt-0.5">
                  {isEdit ? 'Modify Beneficiary Details' : 'Legacy Beneficiary'}
                </p>
             </div>
          </div>
          <button onClick={handleClose} type="button" className="p-2 md:p-3 bg-slate-50 text-slate-400 hover:text-rose-600 rounded-xl md:rounded-2xl transition-all shrink-0">
            <X className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2.5} />
          </button>
        </div>

        <form id="add-nominee-form" onSubmit={handleSubmit} className="p-6 md:p-10 overflow-y-auto custom-scrollbar space-y-8 md:space-y-10">
           
           <div className="space-y-4 md:space-y-6">
              <div className="flex items-center gap-3"><div className="w-1 h-5 md:w-1.5 md:h-6 bg-indigo-600 rounded-full"></div><h3 className="text-base md:text-lg font-black text-slate-900">Personal Identity</h3></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div className="space-y-2">
                    <label className="text-[10px] md:text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Full Name *</label>
                    <input type="text" required value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})} className="w-full px-5 py-3.5 md:px-6 md:py-4 bg-slate-50 text-slate-900 border-2 border-transparent rounded-xl md:rounded-[1.25rem] focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold transition-all" placeholder="As per ID" />
                </div>
                <div className="space-y-2">
                    <label className="text-[10px] md:text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Relationship *</label>
                    <select value={formData.relationship} onChange={(e) => setFormData({...formData, relationship: e.target.value as Relationship})} className="w-full px-5 py-3.5 md:px-6 md:py-4 bg-slate-50 text-slate-900 border-2 border-transparent rounded-xl md:rounded-[1.25rem] focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold transition-all cursor-pointer">
                        {Object.values(Relationship).map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>
                <div className="space-y-2">
                    <label className="text-[10px] md:text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Target Estate Share (%)</label>
                    <div className="relative">
                        <Percent className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                        <input type="number" min="0" max="100" value={formData.targetShare || 0} onChange={(e) => setFormData({...formData, targetShare: Number(e.target.value)})} className="w-full px-5 py-3.5 md:px-6 md:py-4 bg-slate-50 text-slate-900 border-2 border-transparent rounded-xl md:rounded-[1.25rem] focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold transition-all" />
                    </div>
                </div>
                <div className="space-y-2">
                    <label className="text-[10px] md:text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Date of Birth {isChild && '*'}</label>
                    <input type="date" required={isChild} value={formData.dob || ''} onChange={(e) => setFormData({...formData, dob: e.target.value})} className={`w-full px-5 py-3.5 md:px-6 md:py-4 border-2 rounded-xl md:rounded-[1.25rem] focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold transition-all ${isChild && !formData.dob ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-transparent'}`} />
                </div>
              </div>
           </div>

           <div className="space-y-4 md:space-y-6">
              <div className="flex items-center gap-3"><div className="w-1 h-5 md:w-1.5 md:h-6 bg-indigo-600 rounded-full"></div><h3 className="text-base md:text-lg font-black text-slate-900">Contact & Legacy Access</h3></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                 <div className="space-y-2">
                    <label className="text-[10px] md:text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Email (Required for Discovery) *</label>
                    <input type="email" required value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-5 py-3.5 md:px-6 md:py-4 bg-slate-50 text-slate-900 border-2 border-transparent rounded-xl md:rounded-[1.25rem] focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold" placeholder="nominee@example.com" />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] md:text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Mobile Number</label>
                    <input type="tel" value={formData.mobile || ''} onChange={(e) => setFormData({...formData, mobile: e.target.value})} className="w-full px-5 py-3.5 md:px-6 md:py-4 bg-slate-50 text-slate-900 border-2 border-transparent rounded-xl md:rounded-[1.25rem] focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold" placeholder="+91 XXXX XXXX" />
                 </div>
              </div>
           </div>

           <div className="space-y-4 md:space-y-6">
              <div className="flex items-center gap-3"><div className="w-1 h-5 md:w-1.5 md:h-6 bg-indigo-600 rounded-full"></div><h3 className="text-base md:text-lg font-black text-slate-900">Legal Identification</h3></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                 <div className="space-y-2">
                    <label className="text-[10px] md:text-xs font-black text-slate-400 uppercase tracking-widest ml-1">PAN {formData.isMinor ? '(Guardian)' : ''}</label>
                    <input type="text" value={formData.pan || ''} onChange={(e) => setFormData({...formData, pan: e.target.value.toUpperCase()})} className="w-full px-5 py-3.5 md:px-6 md:py-4 bg-slate-50 text-slate-900 border-2 border-transparent rounded-xl md:rounded-[1.25rem] focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold uppercase" placeholder="ABCDE1234F" />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] md:text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Aadhaar Number</label>
                    <input type="text" value={formData.aadhaar || ''} onChange={(e) => setFormData({...formData, aadhaar: e.target.value})} className="w-full px-5 py-3.5 md:px-6 md:py-4 bg-slate-50 text-slate-900 border-2 border-transparent rounded-xl md:rounded-[1.25rem] focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold" placeholder="XXXX-XXXX-XXXX" />
                 </div>
              </div>
           </div>

           {formData.isMinor && (
             <div className="space-y-4 md:space-y-6 bg-amber-50 p-6 md:p-8 rounded-2xl md:rounded-[2rem] border border-amber-100 animate-fade-in">
                <div className="flex items-center gap-3"><h3 className="text-base md:text-lg font-black text-amber-900">Minor Guardian</h3></div>
                <div className="flex items-start gap-2 p-3 bg-amber-100/50 rounded-xl text-[10px] text-amber-700 font-bold mb-2">
                    <AlertCircle size={14} className="shrink-0 mt-0.5" /><span>Nominee is under 18. Guardian required.</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div className="space-y-2">
                        <label className="text-[10px] md:text-xs font-black text-amber-700/60 uppercase tracking-widest ml-1">Guardian Name *</label>
                        <input type="text" required value={formData.guardianName || ''} onChange={(e) => setFormData({...formData, guardianName: e.target.value})} className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm font-bold text-amber-900" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] md:text-xs font-black text-amber-700/60 uppercase tracking-widest ml-1">Relationship *</label>
                        <input type="text" required value={formData.guardianRelationship || ''} onChange={(e) => setFormData({...formData, guardianRelationship: e.target.value})} className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm font-bold text-amber-900" />
                    </div>
                </div>
             </div>
           )}
        </form>

        {/* Action Footer */}
        <div className="p-6 md:p-8 border-t border-slate-100 bg-white flex flex-col sm:flex-row justify-end items-center gap-3 md:gap-6 shrink-0">
            <button type="button" onClick={handleClose} className="w-full sm:w-auto px-6 py-3 rounded-xl text-slate-400 font-bold text-xs uppercase tracking-widest">Discard</button>
            <button form="add-nominee-form" type="submit" disabled={!isFormValid} className={`w-full sm:w-auto relative group px-8 py-4 md:px-10 md:py-5 rounded-xl md:rounded-[2rem] font-black text-xs md:text-sm uppercase tracking-[0.15em] transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden ${isFormValid ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-100 hover:-translate-y-1 active:scale-95' : 'bg-slate-100 text-slate-400 shadow-none'}`}>
                {isEdit ? <Save size={18} /> : <Shield size={18} className={isFormValid ? 'text-indigo-200' : 'text-slate-300'} />}
                <span>{isEdit ? 'Update Profile' : 'Confirm'}</span>
                <ChevronRight size={18} strokeWidth={3} className={`transition-transform duration-300 ${isFormValid ? 'group-hover:translate-x-1' : ''}`} />
            </button>
        </div>
      </div>
    </div>
  );
};
